import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import EmailRoundedIcon from '@mui/icons-material/EmailRounded';
import PhoneRoundedIcon from '@mui/icons-material/PhoneRounded';
import SmsRoundedIcon from '@mui/icons-material/SmsRounded';
import LoginRoundedIcon from '@mui/icons-material/LoginRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import { Box } from '@mui/material';
import { accountService } from '../../services/api/accountService';
import { serviceWizardSearchRequestService } from '../../services/api/serviceWizardSearchRequestService';
import { getOrCreateWizardClientSessionId } from '../../utils/wizardClientSession';
import { persistActiveWizardSearchRequest } from '../../utils/persistActiveWizardSearchRequest';
import {
  formatPhoneInputMask,
  phoneDigitsForApi,
} from '../../utils/phoneInputMask';
import {
  getSignupEmailError,
  getSignupPhoneError,
  SIGNUP_EMAIL_REGEX,
} from '../../utils/signupFieldValidation';
import { getNavigateToLoginOptions } from '../../utils/authSessionExpired';
import { setSendInquiryResume } from '../../utils/sendInquiryResume';
import {
  readLatestWizardRequesterCache,
  readWizardRequesterCache,
  writeWizardRequesterCache,
  wizardRequesterPatchMatchesSaved,
} from '../../utils/wizardRequesterSessionCache';
import { toPersonNameCase } from '../../utils/normalizePersonName';
import { getSearchTimingOptions, withSearchTiming } from '../../utils/serviceSearch';
import {
  getServiceLocationErrors,
  isServiceLocationComplete,
  readLastServiceLocation,
  withServiceLocation,
  writeLastServiceLocation,
} from '../../utils/serviceLocation';
import ServiceLocationFields from '../common/ServiceLocationFields/ServiceLocationFields';
import {
  emptyMatchHeadline,
  emptyMatchSubcopy,
  EMPTY_MATCH_BUSINESS_ID,
  hasWaitlistSubmitted,
  markWaitlistSubmitted,
  waitlistAlreadySubmittedMessage,
  waitlistSuccessMessage,
} from '../../utils/emptyMatchWaitlist';
import NkySpinner from '../common/NkySpinner/NkySpinner';
import {
  ContactErrorAlert,
  ContactExistingAccountBody,
  ContactExistingAccountIconWrap,
  ContactExistingAccountMessage,
  ContactExistingAccountPanel,
  ContactExistingAccountTitle,
  ContactChoiceOptions,
  ContactField,
  ContactMethodButton,
  ContactMethodLabel,
  ContactMethodOptions,
  ContactMethodSavedNote,
  ContactMethodSection,
  ContactSignInButton,
} from '../common/ContactForServiceModal/ContactForServiceModal.styled';
import {
  PreferenceError,
  WaitlistCard,
  WaitlistContactRow,
  WaitlistFields,
  WaitlistFullRow,
  WaitlistHeadline,
  WaitlistSubcopy,
  WaitlistSubmitButton,
  WaitlistSuccess,
  WaitlistSuccessBody,
  WaitlistSuccessIconWrap,
  WaitlistSuccessText,
  WaitlistSuccessTitle,
} from './EmptyMatchWaitlist.styled';

const PREFERRED_CONTACT_METHODS = ['email', 'phone', 'text'];

function isPreferredContactMethod(value) {
  return PREFERRED_CONTACT_METHODS.includes(value);
}

function preferredContactMethodLabel(method) {
  if (method === 'email') return 'email';
  if (method === 'phone') return 'phone';
  if (method === 'text') return 'text';
  return method;
}

function optionalPhoneError(phoneValue) {
  return getSignupPhoneError(phoneValue, { requiredLabel: '' });
}

function resolveWaitlistSessionId(explicit) {
  if (explicit) return explicit;
  try {
    return getOrCreateWizardClientSessionId() || '';
  } catch {
    return '';
  }
}

function EmptyMatchWaitlist({
  tradeLabel,
  hasOtherProviders = false,
  searchRequestId = '',
  wizardState = null,
  clientSessionId = '',
  user = null,
  headline = '',
  subcopy = '',
  getSubmitError = null,
  children = null,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [preferredContactMethod, setPreferredContactMethod] = useState('');
  const [contactMethodLocked, setContactMethodLocked] = useState(false);
  const [contactMethodLoading, setContactMethodLoading] = useState(false);
  const [nameError, setNameError] = useState('');
  const [preferenceError, setPreferenceError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [formError, setFormError] = useState('');
  const [emailHasAccount, setEmailHasAccount] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [successAnimated, setSuccessAnimated] = useState(true);
  const [checkingPriorSubmit, setCheckingPriorSubmit] = useState(
    Boolean(searchRequestId),
  );
  /** Request created on submit when the search had none yet (kept for the sign-in resume). */
  const [createdRequestId, setCreatedRequestId] = useState('');
  const activeRequestId = searchRequestId || createdRequestId;
  const sessionId = resolveWaitlistSessionId(clientSessionId);
  /** A new request is saved on submit, so this form asks where the job is (and timing if missing). */
  const asksJobDetails = !searchRequestId && Boolean(wizardState);
  const asksTiming = asksJobDetails && !wizardState.formData?.urgency;
  const [jobLocation, setJobLocation] = useState(
    () => readLastServiceLocation() || { city: '', state: '' },
  );
  const [locationErrors, setLocationErrors] = useState({ city: '', state: '' });
  const [timing, setTiming] = useState('');
  const [timingError, setTimingError] = useState('');

  useEffect(() => {
    if (!searchRequestId) {
      setCheckingPriorSubmit(false);
      return undefined;
    }
    if (hasWaitlistSubmitted(searchRequestId)) {
      setSuccessMessage(
        waitlistAlreadySubmittedMessage(tradeLabel, hasOtherProviders),
      );
      setSuccessAnimated(false);
      setCheckingPriorSubmit(false);
      return undefined;
    }

    let cancelled = false;
    setCheckingPriorSubmit(true);
    (async () => {
      try {
        const doc = await serviceWizardSearchRequestService.getById(searchRequestId, {
          clientSessionId: sessionId || undefined,
        });
        if (cancelled || !doc?.helpRequested) return;
        markWaitlistSubmitted(searchRequestId);
        setSuccessMessage(
          waitlistAlreadySubmittedMessage(tradeLabel, hasOtherProviders),
        );
        setSuccessAnimated(false);
      } catch {
        /* keep the form if lookup fails */
      } finally {
        if (!cancelled) setCheckingPriorSubmit(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [searchRequestId, sessionId, tradeLabel, hasOtherProviders]);

  useEffect(() => {
    if (!user) {
      setContactMethodLoading(false);
      setContactMethodLocked(false);
      const cached =
        readWizardRequesterCache(searchRequestId) || readLatestWizardRequesterCache();
      if (cached) {
        setFirstName(cached.firstName || '');
        setLastName(cached.lastName || '');
        setEmail(cached.email || '');
        setPhone(formatPhoneInputMask(cached.phoneInput ?? ''));
      }
      return undefined;
    }

    let cancelled = false;
    setContactMethodLoading(true);
    (async () => {
      try {
        const acc = await accountService.getAccount();
        if (cancelled) return;
        setFirstName(acc.FirstName || '');
        setLastName(acc.LastName || '');
        setEmail(acc.Email || '');
        setPhone(formatPhoneInputMask(acc.PhoneNumber || ''));
        if (isPreferredContactMethod(acc.PreferredContactMethod)) {
          setPreferredContactMethod(acc.PreferredContactMethod);
          setContactMethodLocked(true);
        }
      } catch {
        if (cancelled) return;
        setEmail(user.Email || '');
      } finally {
        if (!cancelled) setContactMethodLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, searchRequestId]);

  const handlePreference = (method) => {
    if (contactMethodLocked) return;
    setPreferredContactMethod(method);
    setPreferenceError('');
    setEmailError('');
    setPhoneError('');
  };

  const handleGoToSignIn = () => {
    if (activeRequestId && sessionId) {
      setSendInquiryResume({
        returnPath: `${location.pathname}${location.search}`,
        wizardSearchRequestId: activeRequestId,
        clientSessionId: sessionId,
      });
    }
    navigate('/login', getNavigateToLoginOptions(location));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setFormError('');
    setEmailHasAccount(false);
    if (!searchRequestId && !wizardState) {
      setFormError('Start a search before joining the list.');
      return;
    }
    const extraError = getSubmitError ? getSubmitError() : '';
    if (extraError) {
      setFormError(extraError);
      return;
    }
    if (asksJobDetails) {
      setLocationErrors(getServiceLocationErrors(jobLocation));
      if (!isServiceLocationComplete(jobLocation)) {
        setFormError('Please enter the city and state where the job is.');
        return;
      }
    }
    if (asksTiming && !timing) {
      setTimingError('Choose when you need help.');
      return;
    }
    if (!preferredContactMethod) {
      setPreferenceError('Select how we should contact you.');
      return;
    }
    setPreferenceError('');

    setSubmitting(true);
    let requestId = searchRequestId;
    /** Saves the search on first send (after the form validates) so no empty requests are created. */
    const ensureRequestId = async () => {
      if (requestId) return requestId;
      const answered = withServiceLocation(
        asksTiming ? withSearchTiming(wizardState, timing) : wizardState,
        jobLocation,
      );
      requestId = await persistActiveWizardSearchRequest(answered);
      if (!requestId) throw new Error('Could not save your request. Please try again.');
      writeLastServiceLocation(jobLocation);
      setCreatedRequestId(requestId);
      return requestId;
    };
    try {
      if (!user) {
        if (!sessionId) {
          throw new Error('Session expired. Refresh the page and try again.');
        }
        if (!firstName.trim() || !lastName.trim()) {
          setNameError('Please enter your first and last name.');
          return;
        }
        setNameError('');
        const emailErr =
          preferredContactMethod === 'email'
            ? getSignupEmailError(email)
            : '';
        const phoneErr =
          preferredContactMethod === 'phone' || preferredContactMethod === 'text'
            ? getSignupPhoneError(phone)
            : optionalPhoneError(phone);
        setEmailError(emailErr);
        setPhoneError(phoneErr);
        if (emailErr || phoneErr) return;

        const emailTrim = email.trim();
        const digits = phoneDigitsForApi(phone);
        const normalizedFirstName = toPersonNameCase(firstName);
        const normalizedLastName = toPersonNameCase(lastName);
        setFirstName(normalizedFirstName);
        setLastName(normalizedLastName);
        const patchPayload = {
          firstName: normalizedFirstName,
          lastName: normalizedLastName,
          email:
            preferredContactMethod === 'email' && SIGNUP_EMAIL_REGEX.test(emailTrim)
              ? emailTrim
              : '',
          ...(digits.length >= 10 ? { phone: digits } : {}),
        };
        const fingerprint = {
          firstName: patchPayload.firstName,
          lastName: patchPayload.lastName,
          email: patchPayload.email || '',
          phone: patchPayload.phone != null ? String(patchPayload.phone) : '',
        };
        await ensureRequestId();
        const cached = readWizardRequesterCache(requestId);
        const skipPatch =
          cached?.savedPatchFingerprint &&
          wizardRequesterPatchMatchesSaved(fingerprint, cached.savedPatchFingerprint);
        if (!skipPatch) {
          await serviceWizardSearchRequestService.patchRequester(requestId, {
            clientSessionId: sessionId,
            ...patchPayload,
          });
          writeWizardRequesterCache(requestId, {
            firstName: patchPayload.firstName,
            lastName: patchPayload.lastName,
            email: patchPayload.email || '',
            phoneInput: phone,
            savedPatchFingerprint: fingerprint,
          });
        }
      }

      await ensureRequestId();
      await serviceWizardSearchRequestService.sendToBusiness(requestId, {
        businessId: EMPTY_MATCH_BUSINESS_ID,
        preferredContactMethod,
        ...(sessionId && !user ? { clientSessionId: sessionId } : {}),
      });
      markWaitlistSubmitted(requestId);
      setSuccessAnimated(true);
      setSuccessMessage(
        waitlistSuccessMessage(preferredContactMethod, tradeLabel, hasOtherProviders),
      );
    } catch (err) {
      if (!user && err.status === 403) {
        setEmailHasAccount(true);
      } else if (err.status === 409) {
        markWaitlistSubmitted(requestId);
        setSuccessAnimated(false);
        setSuccessMessage(
          waitlistAlreadySubmittedMessage(tradeLabel, hasOtherProviders),
        );
      } else {
        setFormError(err.message || 'Could not save your details. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (checkingPriorSubmit) {
    return (
      <WaitlistCard>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <NkySpinner label="Checking waitlist status" />
        </Box>
      </WaitlistCard>
    );
  }

  if (successMessage) {
    return (
      <WaitlistCard>
        <WaitlistSuccess
          role="status"
          data-testid="empty-match-waitlist-success"
          $animate={successAnimated}
        >
          <WaitlistSuccessIconWrap aria-hidden>
            <CheckCircleRoundedIcon />
          </WaitlistSuccessIconWrap>
          <WaitlistSuccessBody>
            <WaitlistSuccessTitle>You&apos;re on the list</WaitlistSuccessTitle>
            <WaitlistSuccessText>{successMessage}</WaitlistSuccessText>
          </WaitlistSuccessBody>
        </WaitlistSuccess>
      </WaitlistCard>
    );
  }

  const phoneSelected =
    preferredContactMethod === 'phone' || preferredContactMethod === 'text';
  const sendDisabled = submitting || emailHasAccount || contactMethodLoading;

  return (
    <WaitlistCard>
      <WaitlistHeadline>{headline || emptyMatchHeadline()}</WaitlistHeadline>
      <WaitlistSubcopy>
        {subcopy || emptyMatchSubcopy(tradeLabel, hasOtherProviders)}
      </WaitlistSubcopy>
      <form onSubmit={handleSubmit} noValidate>
        {formError ? <ContactErrorAlert severity="error">{formError}</ContactErrorAlert> : null}
        {emailHasAccount ? (
          <ContactExistingAccountPanel role="status">
            <ContactExistingAccountIconWrap aria-hidden>
              <LoginRoundedIcon />
            </ContactExistingAccountIconWrap>
            <ContactExistingAccountBody>
              <ContactExistingAccountTitle>Account found</ContactExistingAccountTitle>
              <ContactExistingAccountMessage>
                An account already exists for this email. Sign in to send your request with your
                saved profile—we&apos;ll return you here after you log in.
              </ContactExistingAccountMessage>
              <ContactSignInButton variant="contained" disableElevation onClick={handleGoToSignIn}>
                Sign in
              </ContactSignInButton>
            </ContactExistingAccountBody>
          </ContactExistingAccountPanel>
        ) : null}
        <WaitlistFields>
          {children ? <WaitlistFullRow>{children}</WaitlistFullRow> : null}
          {asksJobDetails ? (
            <WaitlistFullRow>
              <ServiceLocationFields
                city={jobLocation.city}
                state={jobLocation.state}
                errors={locationErrors}
                onChange={(next) => {
                  setLocationErrors((prev) => ({
                    city: next.city !== jobLocation.city ? '' : prev.city,
                    state: next.state !== jobLocation.state ? '' : prev.state,
                  }));
                  setJobLocation(next);
                }}
              />
            </WaitlistFullRow>
          ) : null}
          {asksTiming ? (
            <WaitlistFullRow>
              <ContactMethodLabel>When do you need help?</ContactMethodLabel>
              <ContactChoiceOptions role="radiogroup" aria-label="Timing">
                {getSearchTimingOptions().map((option) => (
                  <ContactMethodButton
                    key={option.id}
                    type="button"
                    role="radio"
                    aria-checked={timing === option.id}
                    $selected={timing === option.id}
                    onClick={() => {
                      setTiming(option.id);
                      setTimingError('');
                    }}
                  >
                    {option.searchLabel}
                  </ContactMethodButton>
                ))}
              </ContactChoiceOptions>
              {timingError ? <PreferenceError role="alert">{timingError}</PreferenceError> : null}
            </WaitlistFullRow>
          ) : null}
          <WaitlistContactRow>
            <ContactMethodSection>
              {contactMethodLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                  <NkySpinner size="sm" label="Loading preferred contact method" />
                </Box>
              ) : contactMethodLocked ? (
                <ContactMethodSavedNote>
                  Your preferred contact method is{' '}
                  <strong>{preferredContactMethodLabel(preferredContactMethod)}</strong>.
                  {' '}
                  You can change it on the{' '}
                  <Link to="/account" target="_blank" rel="noopener noreferrer">
                    account page
                  </Link>
                  .
                </ContactMethodSavedNote>
              ) : (
                <>
                  <ContactMethodLabel>How should we contact you?</ContactMethodLabel>
                  <ContactMethodOptions role="radiogroup" aria-label="Preferred contact method">
                    <ContactMethodButton
                      type="button"
                      role="radio"
                      aria-checked={preferredContactMethod === 'email'}
                      $selected={preferredContactMethod === 'email'}
                      onClick={() => handlePreference('email')}
                    >
                      <EmailRoundedIcon />
                      Email
                    </ContactMethodButton>
                    <ContactMethodButton
                      type="button"
                      role="radio"
                      aria-checked={preferredContactMethod === 'phone'}
                      $selected={preferredContactMethod === 'phone'}
                      onClick={() => handlePreference('phone')}
                    >
                      <PhoneRoundedIcon />
                      Phone
                    </ContactMethodButton>
                    <ContactMethodButton
                      type="button"
                      role="radio"
                      aria-checked={preferredContactMethod === 'text'}
                      $selected={preferredContactMethod === 'text'}
                      onClick={() => handlePreference('text')}
                    >
                      <SmsRoundedIcon />
                      Text
                    </ContactMethodButton>
                  </ContactMethodOptions>
                  {preferenceError ? (
                    <PreferenceError role="alert">{preferenceError}</PreferenceError>
                  ) : null}
                </>
              )}
            </ContactMethodSection>
          </WaitlistContactRow>
          {!user ? (
            <>
              <ContactField
                label="First name"
                value={firstName}
                onChange={(event) => {
                  setFirstName(event.target.value);
                  if (nameError) setNameError('');
                }}
                onBlur={(event) => {
                  const normalized = toPersonNameCase(event.target.value);
                  if (normalized !== event.target.value) setFirstName(normalized);
                }}
                required
                fullWidth
                size="small"
                error={Boolean(nameError)}
              />
              <ContactField
                label="Last name"
                value={lastName}
                onChange={(event) => {
                  setLastName(event.target.value);
                  if (nameError) setNameError('');
                }}
                onBlur={(event) => {
                  const normalized = toPersonNameCase(event.target.value);
                  if (normalized !== event.target.value) setLastName(normalized);
                }}
                required
                fullWidth
                size="small"
                error={Boolean(nameError)}
                helperText={nameError || undefined}
              />
              {phoneSelected ? null : (
                <ContactField
                  label="Email"
                  type="email"
                  value={email}
                  onChange={(event) => {
                    setEmail(event.target.value);
                    if (emailError) setEmailError('');
                  }}
                  required={preferredContactMethod === 'email'}
                  fullWidth
                  size="small"
                  error={Boolean(emailError)}
                  helperText={emailError || undefined}
                  autoComplete="email"
                />
              )}
              <ContactField
                label={`Phone${preferredContactMethod === 'email' ? ' (optional)' : ''}`}
                type="tel"
                value={phone}
                onChange={(event) => {
                  setPhone(formatPhoneInputMask(event.target.value));
                  if (phoneError) setPhoneError('');
                }}
                required={phoneSelected}
                fullWidth
                size="small"
                error={Boolean(phoneError)}
                helperText={phoneError || undefined}
                autoComplete="tel"
                inputProps={{ inputMode: 'tel' }}
              />
            </>
          ) : null}
          <WaitlistFullRow>
            <WaitlistSubmitButton type="submit" disabled={sendDisabled} variant="contained">
              {submitting ? 'Sending…' : 'Notify me'}
            </WaitlistSubmitButton>
          </WaitlistFullRow>
        </WaitlistFields>
      </form>
    </WaitlistCard>
  );
}

EmptyMatchWaitlist.propTypes = {
  tradeLabel: PropTypes.string.isRequired,
  hasOtherProviders: PropTypes.bool,
  /** Existing search request; when empty, `wizardState` is saved as a new request on submit. */
  searchRequestId: PropTypes.string,
  wizardState: PropTypes.object,
  clientSessionId: PropTypes.string,
  user: PropTypes.shape({
    Email: PropTypes.string,
  }),
  headline: PropTypes.string,
  subcopy: PropTypes.string,
  /** Extra validation for fields passed as `children`; return an error message or ''. */
  getSubmitError: PropTypes.func,
  /** Extra fields shown above the contact fields (e.g. a job description). */
  children: PropTypes.node,
};

export default EmptyMatchWaitlist;
